import { KeyValueStorageTypeConfiguration } from 'record-it-utils-custom-types';
import { assertNotNullOrUndefined } from '../utils/assert/assertNotNullOrUndefined';
import {
  KeyValueStorageStrategy,
  Item
} from './KeyValueStorageStrategy/KeyValueStorageStrategy';

export class KeyValueStorage {
  private static strategy: KeyValueStorageStrategy | null = null;
  private static defaultTableName: TableName | null = null;
  private static allTableConfigurations: Array<TableConfigurationWithName> = [];

  private constructor() {}

  public static async init(
    strategy: KeyValueStorageStrategy,
    options: InitOptions
  ): Promise<void> {
    this.allTableConfigurations = Object.entries(
      options.tableConfigurationMap
    ).map<TableConfigurationWithName>(([name, config]) => ({
      ...config,
      name: name as TableName
    }));
    const defaultTableConfiguration = this.getDefaultTableConfiguration(
      this.allTableConfigurations
    );
    this.defaultTableName = defaultTableConfiguration.name;

    this.strategy = strategy;
    await this.strategy.initStore(
      options.storeName,
      this.allTableConfigurations.map((c) => c.name)
    );
  }

  public static async getItem<T>(
    key: string,
    tableName?: OptionalTableName
  ): Promise<T | undefined> {
    return this.getStrategy().getItem(
      key,
      this.getTableNameOrDefault(tableName)
    );
  }

  public static async setItem(
    key: string,
    value: unknown,
    tableName?: OptionalTableName
  ): Promise<void> {
    await this.getStrategy().setItem(
      key,
      value,
      this.getTableNameOrDefault(tableName)
    );
  }

  public static async removeItem(
    key: string,
    tableName?: OptionalTableName
  ): Promise<void> {
    await this.getStrategy().removeItem(
      key,
      this.getTableNameOrDefault(tableName)
    );
  }

  public static async removeItems(
    keys: Array<string>,
    tableName?: OptionalTableName
  ): Promise<void> {
    await this.getStrategy().removeItems(
      keys,
      this.getTableNameOrDefault(tableName)
    );
  }

  public static async getItems<T = unknown>(
    tableName?: OptionalTableName
  ): Promise<Array<T>> {
    return this.getStrategy().getItems(this.getTableNameOrDefault(tableName));
  }

  public static async setItems<T = unknown>(
    items: Array<Item<T>>,
    tableName?: OptionalTableName
  ): Promise<void> {
    await this.getStrategy().setItems(
      items,
      this.getTableNameOrDefault(tableName)
    );
  }

  public static async clear(tableName?: OptionalTableName): Promise<void> {
    await this.getStrategy().clear(this.getTableNameOrDefault(tableName));
  }

  public static async clearAll(
    ignoreKeepOnClearAll: boolean = false
  ): Promise<void> {
    for (const tableConfiguration of this.allTableConfigurations) {
      if (!tableConfiguration.keepOnClearAll || ignoreKeepOnClearAll) {
        await this.clear(tableConfiguration.name);
      }
    }
  }

  private static getTableNameOrDefault(
    tableName: OptionalTableName
  ): TableName {
    return tableName ?? this.getDefaultTableName();
  }

  private static getDefaultTableName(): TableName {
    assertNotNullOrUndefined(
      this.defaultTableName,
      'KeyValueStorage is not initialized'
    );
    return this.defaultTableName;
  }

  private static getStrategy(): KeyValueStorageStrategy {
    assertNotNullOrUndefined(
      this.strategy,
      'KeyValueStorage is not initialized'
    );
    return this.strategy;
  }

  private static getDefaultTableConfiguration(
    tableConfigurations: Array<TableConfigurationWithName>
  ): TableConfigurationWithName {
    let defaultConfig: TableConfigurationWithName | null = null;

    for (const config of tableConfigurations) {
      if (config.default) {
        if (defaultConfig) {
          // eslint-disable-next-line no-console
          console.warn(
            `already found a default table. First found defaultTable: ${defaultConfig.name}. Newly found table: ${config.name}.`
          );
        } else {
          defaultConfig = config;
        }
      }
    }

    if (!defaultConfig) {
      throw new Error('no default table configuration found');
    }

    return defaultConfig;
  }
}

export type TableName = KeyValueStorageTypeConfiguration['TableName'];
export type OptionalTableName = TableName | null | undefined;

export type InitOptions = {
  storeName: string;
  tableConfigurationMap: TableConfigurationMap;
};

export type TableConfigurationMap = Record<TableName, TableConfiguration>;

export type TableConfiguration = {
  default?: boolean;
  /** if this is true, then the table will not be cleared on a clearAll operation */
  keepOnClearAll?: boolean;
};

export type TableConfigurationWithName = TableConfiguration & {
  name: TableName;
};
